import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, FolderKanban, Banknote, ShieldCheck, Settings as SettingsIcon, X,
  Briefcase, FileText, ClipboardList, CheckSquare, BarChart3, Receipt, Users as UsersIcon,
  Building2, Truck, Wallet, Package, Calculator, BookOpen, Landmark, Coins, MinusCircle,
  Sparkles, Activity, Hammer, FileSearch, Layers, ClipboardCheck, ShieldAlert, Boxes,
  TrendingUp, FileSpreadsheet, Cog, UserCheck, ListChecks, Database, MapPin, ScanLine,
  GitBranch, ClipboardSignature, LogOut, User as UserIcon, Download, DollarSign,
  ChevronRight, ShoppingCart, HardHat, Compass, Tractor, Users as HRIcon,
} from 'lucide-react';
import { axiosInstance } from '../App';

/**
 * PilotSidebar — Overlay Layer (now permission-aware).
 *
 * ▸ Admin role               → renders nothing here (Layout shows full sidebar).
 * ▸ Non-admin with PILOT_NAV → shows ONLY the pages granted via Role/User
 *                              Permissions. The admin controls the catalog
 *                              from Settings → Role Permissions or
 *                              Settings → Employee Permissions.
 *
 * Pages are mapped to canonical app routes + Lucide icons via PAGE_REGISTRY.
 * Any page id present in the user's permission set but missing from the
 * registry is silently skipped — never crashes the sidebar.
 *
 * Tenant identity is sourced from `/api/companies/me/active` so the
 * sidebar header reflects the user's own company, never the dev tenant.
 */

// One registry per page id — keep this in sync with `AVAILABLE_PAGES`
// (`backend/models/permissions.py`). Pages without a registry entry stay
// hidden in the Pilot sidebar (admins still reach them via the full
// sidebar or by typing the URL).
const PAGE_REGISTRY = {
  dashboard:                     { path: '/',                                  icon: LayoutDashboard,    en: 'Dashboard',                ar: 'لوحة التحكم' },
  cfo_dashboard:                 { path: '/cfo-dashboard',                     icon: TrendingUp,         en: 'CFO Dashboard',            ar: 'لوحة المدير المالي' },
  cfo_monthly_pack:              { path: '/cfo-monthly-pack',                  icon: FileSpreadsheet,    en: 'CFO Monthly Pack',         ar: 'الحزمة الشهرية' },
  ai_decision_cockpit:           { path: '/ai-decision-cockpit',               icon: Sparkles,           en: 'AI Decision Cockpit',      ar: 'مركز قرارات الذكاء' },
  action_center:                 { path: '/action-center',                     icon: Activity,           en: 'Action Center',            ar: 'مركز الإجراءات' },
  approvals:                     { path: '/approvals',                         icon: CheckSquare,        en: 'Approvals',                ar: 'الموافقات' },
  my_tasks:                      { path: '/my-tasks',                          icon: ListChecks,         en: 'My Tasks',                 ar: 'مهامي' },
  notifications:                 { path: '/notifications',                     icon: Activity,           en: 'Notifications',            ar: 'الإشعارات' },
  instant_reports:               { path: '/instant-reports',                   icon: BarChart3,          en: 'Instant Reports',          ar: 'تقارير فورية' },
  expenses_report:               { path: '/expenses-report',                   icon: BarChart3,          en: 'Expenses Report',          ar: 'تقرير المصروفات' },
  cash_expenses_ledger:          { path: '/cash-expenses-ledger',              icon: DollarSign,         en: 'Cash Expenses Ledger',     ar: 'دفتر المصروفات النقدية' },

  projects:                      { path: '/projects',                          icon: FolderKanban,       en: 'Projects',                 ar: 'المشاريع' },
  project_execution_center:      { path: '/project-execution-center',          icon: Hammer,             en: 'Project Execution Center', ar: 'مركز تنفيذ المشاريع' },
  contracts:                     { path: '/contracts',                         icon: Briefcase,          en: 'Contracts',                ar: 'العقود' },
  contract_execution:            { path: '/contract-execution',                icon: ClipboardCheck,     en: 'Contract Execution',       ar: 'تنفيذ العقد' },
  progress_claims:               { path: '/progress-claims',                   icon: ClipboardSignature, en: 'Progress Claims',          ar: 'مطالبات الإنجاز' },
  variation_orders:              { path: '/variation-orders',                  icon: GitBranch,          en: 'Variation Orders',         ar: 'أوامر التغيير' },
  budgets:                       { path: '/budgets',                           icon: BarChart3,          en: 'Budgets',                  ar: 'الموازنات' },
  equipment:                     { path: '/equipment',                         icon: Truck,              en: 'Equipment',                ar: 'المعدات' },
  cost_allocation:               { path: '/cost-allocation',                   icon: Calculator,         en: 'Cost Allocation',          ar: 'توزيع التكاليف' },

  rfq:                           { path: '/customer-rfqs',                     icon: FileSearch,         en: 'Request for Quotation',    ar: 'طلب عرض سعر' },
  quotations:                    { path: '/quotations',                        icon: FileText,           en: 'Quotations',               ar: 'عروض الأسعار' },
  sales_quotations:              { path: '/sales-quotations',                  icon: FileText,           en: 'Sales Quotations',         ar: 'عروض البيع للعملاء' },
  quotation_analytics:           { path: '/quotation-analytics',               icon: BarChart3,          en: 'Quotation Analytics',      ar: 'تحليلات العروض' },
  customers:                     { path: '/customers',                         icon: UsersIcon,          en: 'Customers',                ar: 'العملاء' },
  invoices:                      { path: '/invoices',                          icon: Receipt,            en: 'Invoices',                 ar: 'الفواتير' },
  commercial_hub:                { path: '/commercial-hub',                    icon: Building2,          en: 'Commercial Hub',           ar: 'المركز التجاري' },
  payments:                      { path: '/payments',                          icon: Wallet,             en: 'Payments',                 ar: 'المدفوعات' },
  customer_report:               { path: '/customer-report',                   icon: BarChart3,          en: 'Customer Report',          ar: 'تقرير العملاء' },
  customer_statement:            { path: '/customer-statement',                icon: Receipt,            en: 'Customer Statement',       ar: 'كشف حساب عميل' },
  credit_control:                { path: '/credit-control',                    icon: ShieldAlert,        en: 'Credit Control',           ar: 'متابعة المدينين' },

  suppliers:                     { path: '/suppliers',                         icon: Building2,          en: 'Suppliers',                ar: 'الموردون' },
  vendor_quotations:             { path: '/vendor-quotations',                 icon: FileText,           en: 'Vendor Quotations',        ar: 'عروض الموردين' },
  supplier_offers_comparison:    { path: '/supplier-offers-comparison',        icon: Layers,             en: 'Compare Supplier Offers',  ar: 'مقارنة عروض الموردين' },
  purchase_orders:               { path: '/purchase-orders',                   icon: ClipboardList,      en: 'Purchase Orders',          ar: 'أوامر الشراء' },
  supplier_bills:                { path: '/supplier-bills',                    icon: Receipt,            en: 'Supplier Bills',           ar: 'فواتير الموردين' },
  supplier_report:               { path: '/supplier-report',                   icon: BarChart3,          en: 'Supplier Report',          ar: 'تقرير الموردين' },
  supplier_statement:            { path: '/supplier-statement',                icon: Receipt,            en: 'Supplier Statement',       ar: 'كشف حساب مورد' },

  accounts:                      { path: '/accounts',                          icon: BookOpen,           en: 'Accounts',                 ar: 'الحسابات' },
  journal:                       { path: '/journal',                           icon: BookOpen,           en: 'Journal',                  ar: 'القيود اليومية' },
  bank:                          { path: '/bank',                              icon: Landmark,           en: 'Bank',                     ar: 'البنك' },
  petty_cash:                    { path: '/petty-cash',                        icon: Coins,              en: 'Petty Cash',               ar: 'العهد النقدية' },
  expenses:                      { path: '/expenses',                          icon: MinusCircle,        en: 'Expenses',                 ar: 'المصروفات' },
  credit_notes:                  { path: '/credit-notes',                      icon: FileText,           en: 'Credit Notes',             ar: 'إشعارات دائنة' },
  accounting_reports:            { path: '/accounting-reports',                icon: BarChart3,          en: 'Accounting Reports',       ar: 'التقارير المحاسبية' },
  vat_return:                    { path: '/vat-return',                        icon: FileText,           en: 'VAT Return',               ar: 'إقرار الضريبة' },
  vat_claims:                    { path: '/vat-claims',                        icon: Receipt,            en: 'VAT Claim Management',     ar: 'إدارة المطالبات الضريبية' },
  bills_center:                  { path: '/bills-center',                      icon: Receipt,            en: 'Bills Center',             ar: 'مَركَز الفَواتير' },
  expense_categories:            { path: '/expense-categories',                icon: Receipt,            en: 'Expense Categories',       ar: 'تَصنيفات المَصاريف' },
  government_services:           { path: '/government-services',               icon: Receipt,            en: 'Government Services',      ar: 'الخِدمات الحُكومية' },
  financial_ops:                 { path: '/financial-ops',                     icon: Activity,           en: 'Financial Operations',     ar: 'مَركَز العَمَلِيَّات المالية' },
  bank_reconciliation:           { path: '/bank-reconciliation',               icon: Landmark,           en: 'Bank Reconciliation',      ar: 'المُطابَقة البَنكية' },
  bank_rules:                    { path: '/bank-rules',                        icon: ShieldCheck,        en: 'Bank Rules',               ar: 'قَواعِد المُطابَقة' },
  bank_statements_registry:      { path: '/bank-statements-registry',          icon: Layers,             en: 'Statement Registry',       ar: 'سِجِل الكُشوف البَنكية' },
  bank_import_manager:           { path: '/bank-import-manager',               icon: Layers,             en: 'Bank Import Manager',      ar: 'مُدير استيراد البُنوك' },
  matching_intelligence:         { path: '/matching-intelligence',             icon: Layers,             en: 'Matching Intelligence',    ar: 'ذَكاء المُطابَقَة' },
  document_hub:                  { path: '/document-hub',                      icon: Layers,             en: 'Document Hub',             ar: 'مَركَز المُستَنَدات' },
  financial_timelines:           { path: '/financial-timelines',               icon: Activity,           en: 'Financial Timelines',      ar: 'الخُطوط الزَّمَنيَّة الماليَّة' },

  compliance_center:             { path: '/compliance-center',                 icon: ShieldCheck,        en: 'Compliance Center',        ar: 'مركز الالتزام' },
  audit_trail:                   { path: '/audit-trail',                       icon: ScanLine,           en: 'Audit Trail',              ar: 'مسار التدقيق' },
  zatca:                         { path: '/zatca',                             icon: ShieldCheck,        en: 'ZATCA',                    ar: 'هيئة الزكاة' },
  zatca_setup:                   { path: '/zatca-setup',                       icon: Cog,                en: 'ZATCA Setup',              ar: 'إعداد هيئة الزكاة' },
  month_end:                     { path: '/month-end',                         icon: ClipboardCheck,     en: 'Month-End Closing',        ar: 'إقفال الشهر' },
  year_end_closing:              { path: '/year-end-closing',                  icon: ClipboardCheck,     en: 'Year-End Closing',         ar: 'إقفال السنة' },
  external_accountant_workspace: { path: '/external-accountant',               icon: ShieldCheck,        en: 'External Accountant',      ar: 'المحاسب الخارجي' },

  employees:                     { path: '/employees',                         icon: UsersIcon,          en: 'Employees',                ar: 'الموظفون' },
  payroll:                       { path: '/payroll',                           icon: Wallet,             en: 'Payroll',                  ar: 'الرواتب' },
  employee_accounts:             { path: '/employee-accounts',                 icon: UserCheck,          en: 'Employee Accounts',        ar: 'حسابات الموظفين' },

  inventory:                     { path: '/inventory',                         icon: Package,            en: 'Inventory',                ar: 'المخزون' },
  assets:                        { path: '/assets',                            icon: Boxes,              en: 'Assets',                   ar: 'الأصول' },
  fleet:                         { path: '/fleet',                             icon: Truck,              en: 'Fleet',                    ar: 'الأسطول' },
  field_visits:                  { path: '/field-visits',                      icon: MapPin,             en: 'Field Visits',             ar: 'الزيارات الميدانية' },

  users:                         { path: '/users',                             icon: UsersIcon,          en: 'Users',                    ar: 'المستخدمون' },
  role_permissions:              { path: '/role-permissions',                  icon: ShieldCheck,        en: 'Role Permissions',         ar: 'صلاحيات الأدوار' },
  employee_permissions:          { path: '/employee-permissions',              icon: ShieldCheck,        en: 'Employee Permissions',     ar: 'صلاحيات الموظفين' },
  backup:                        { path: '/backup',                            icon: Database,           en: 'Backup',                   ar: 'النسخ الاحتياطي' },
  governance:                    { path: '/governance',                        icon: ShieldAlert,        en: 'Governance',               ar: 'الحوكمة' },
  settings:                      { path: '/settings',                          icon: SettingsIcon,       en: 'Settings',                 ar: 'الإعدادات' },
};

// Fallback minimal items when /my-permissions returns nothing — guarantees
// the sidebar never renders empty for a freshly-provisioned user.
const FALLBACK_PAGES = ['dashboard', 'projects', 'invoices', 'external_accountant_workspace', 'settings'];

// ─── Pilot Section Grouping (10 categories) ─────────────────────────────
// Same flyout pattern as the main Layout sidebar: clicking a group label
// opens a fixed-position panel beside the sidebar showing its children
// (NOT stacked beneath). Order matches the platform owner's spec.
const PILOT_SECTIONS = [
  { id: 'dashboard',     icon: LayoutDashboard, en: 'Dashboard',           ar: 'لوحة التحكم',           pages: ['dashboard','approvals','my_tasks','notifications','cfo_dashboard','cfo_monthly_pack','action_center','ai_decision_cockpit'] },
  { id: 'sales',         icon: TrendingUp,      en: 'Sales & Revenue',     ar: 'المبيعات والإيرادات',    pages: ['customers','projects','rfq','sales_quotations'] },
  { id: 'procurement',   icon: ShoppingCart,    en: 'Procurement',         ar: 'المشتريات',              pages: ['financial_ops','suppliers','bills_center','expense_categories','government_services','vendor_quotations','supplier_offers_comparison','inventory','quotations','quotation_analytics','contracts'] },
  { id: 'projects_dept', icon: HardHat,         en: 'Projects Department', ar: 'إدارة المشاريع',         pages: ['project_execution_center','cost_allocation','equipment','petty_cash','purchase_orders','payments','supplier_bills','progress_claims','invoices','credit_notes','variation_orders','contract_execution','instant_reports'] },
  { id: 'expenses',      icon: MinusCircle,     en: 'Expenses',            ar: 'المصروفات',              pages: ['expenses','expenses_report'] },
  { id: 'accounting',    icon: BookOpen,        en: 'Accounting & Finance',ar: 'المحاسبة والمالية',     pages: ['accounts','journal','bank','bank_reconciliation','bank_rules','bank_statements_registry','bank_import_manager','matching_intelligence','document_hub','financial_timelines','customer_report','customer_statement','supplier_report','supplier_statement','commercial_hub','credit_control','vat_return','vat_claims','compliance_center','zatca_setup','zatca','month_end','year_end_closing','audit_trail','accounting_reports','cash_expenses_ledger','external_accountant_workspace'] },
  { id: 'hr',            icon: HRIcon,          en: 'Human Resources',     ar: 'الموارد البشرية',        pages: ['employees','payroll','employee_accounts'] },
  { id: 'assets',        icon: Tractor,         en: 'Assets',              ar: 'الأصول',                 pages: ['assets','fleet'] },
  { id: 'field',         icon: Compass,         en: 'Field Operations',    ar: 'العمليات الميدانية',     pages: ['field_visits'] },
  { id: 'admin',         icon: ShieldCheck,     en: 'Administration',      ar: 'الإدارة',                pages: ['governance','users','role_permissions','employee_permissions','backup'] },
];

const PilotSidebar = ({ user, onClose }) => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [identity, setIdentity] = useState(null);
  const [feedbackEmail, setFeedbackEmail] = useState(null);
  const [pages, setPages] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  // Flyout state (mirrors main Layout sidebar): which group is open, its
  // vertical anchor, and whether the user clicked to pin it open.
  const [openGroup, setOpenGroup] = useState(null);
  const [flyoutTop, setFlyoutTop] = useState(0);
  const [pinnedGroup, setPinnedGroup] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axiosInstance.get('/companies/me/active').catch(() => ({ data: null })),
      axiosInstance.get('/pilot/status').catch(() => ({ data: null })),
      axiosInstance.get('/my-permissions').catch(() => ({ data: null })),
    ]).then(([identityRes, statusRes, permRes]) => {
      if (cancelled) return;
      if (identityRes?.data) setIdentity(identityRes.data);
      if (statusRes?.data?.feedback_email) setFeedbackEmail(statusRes.data.feedback_email);
      if (permRes?.data) {
        const p = Array.isArray(permRes.data.pages) ? permRes.data.pages : [];
        setPages(p.length > 0 ? p : FALLBACK_PAGES);
      } else {
        setPages(FALLBACK_PAGES);
      }
    });
    // PWA install prompt — same wiring as full sidebar.
    const beforeInstall = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const appInstalled  = () => { setIsAppInstalled(true); setInstallPrompt(null); };
    window.addEventListener('beforeinstallprompt', beforeInstall);
    window.addEventListener('appinstalled', appInstalled);
    if (window.matchMedia('(display-mode: standalone)').matches) setIsAppInstalled(true);
    return () => {
      cancelled = true;
      window.removeEventListener('beforeinstallprompt', beforeInstall);
      window.removeEventListener('appinstalled', appInstalled);
    };
  }, []);

  const handleLogout = async () => {
    try { await axiosInstance.post('/auth/logout'); } catch { /* ignore */ }
    try { localStorage.removeItem('token'); localStorage.removeItem('user'); } catch { /* ignore */ }
    navigate('/login');
    window.location.reload();
  };

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === 'accepted') setIsAppInstalled(true);
    setInstallPrompt(null);
  };

  const langMode = (() => {
    try { return localStorage.getItem('app_lang') || 'en'; } catch { return 'en'; }
  })();
  const lang = (i18n.language || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';

  const renderLabel = (entry) => {
    if (langMode === 'both') return `${entry.en} / ${entry.ar}`;
    return lang === 'ar' ? entry.ar : entry.en;
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Resolve permission ids → registry entries, grouped into 10 sections.
  const allowed = new Set(pages || FALLBACK_PAGES);
  const groups = PILOT_SECTIONS.map((sec) => ({
    ...sec,
    items: sec.pages
      .filter((pid) => allowed.has(pid) && PAGE_REGISTRY[pid])
      .map((pid) => ({ id: pid, ...PAGE_REGISTRY[pid] })),
  })).filter((sec) => sec.items.length > 0);

  // Extras: any granted page not part of the 10 sections (legacy ids).
  const sectionedIds = new Set(PILOT_SECTIONS.flatMap((s) => s.pages));
  const extras = [...allowed]
    .filter((pid) => !sectionedIds.has(pid) && PAGE_REGISTRY[pid])
    .map((pid) => ({ id: pid, ...PAGE_REGISTRY[pid] }));
  if (extras.length > 0) {
    groups.push({ id: 'extras', icon: FolderKanban, en: 'Other', ar: 'أخرى', items: extras });
  }
  // Settings sits at the bottom of the sidebar inside its own group.
  groups.push({
    id: 'account', icon: UserIcon, en: 'Account', ar: 'الحساب',
    items: [{ id: 'settings', ...PAGE_REGISTRY.settings }],
  });

  const companyName = identity?.name || t('common.loading', 'Loading…');
  const companyLogo = identity?.logo_url;
  const companyVat = identity?.vat_number;

  return (
    <div
      data-testid="pilot-sidebar"
      className="flex flex-col h-full"
    >
      {/* Tenant Identity Header */}
      <div className="p-4 border-b border-gray-200" data-testid="pilot-sidebar-identity">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {companyLogo ? (
              <img
                src={companyLogo}
                alt={`${companyName} Logo`}
                className="w-10 h-10 object-contain rounded"
                data-testid="pilot-tenant-logo"
              />
            ) : (
              <div className="w-10 h-10 rounded bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                {(companyName || '?').slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <h2 className="font-bold text-gray-900 text-sm truncate" data-testid="pilot-tenant-name">
                {companyName}
              </h2>
              {companyVat && (
                <p className="text-xs text-gray-500 truncate">VAT: {companyVat}</p>
              )}
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              data-testid="pilot-sidebar-close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Navigation — 10 collapsible groups with side flyout panels.
          Same UX as the main Layout sidebar: hover (or click to pin)
          opens a fixed-position panel beside the sidebar showing the
          group's children — they do NOT stack beneath the group label. */}
      <nav className="flex-1 p-3 overflow-y-auto">
        <div className="space-y-1">
          {groups.map((group) => {
            const GroupIcon = group.icon || FolderKanban;
            const hasActive = group.items.some((it) => isActive(it.path));
            const isOpen = openGroup === group.id;
            const groupLabel =
              langMode === 'both' ? `${group.en} / ${group.ar}`
              : lang === 'ar' ? group.ar : group.en;
            return (
              <div
                key={group.id}
                className="select-none relative"
                onMouseEnter={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setFlyoutTop(r.top);
                  setOpenGroup(group.id);
                }}
                onMouseLeave={() => { if (!pinnedGroup) setOpenGroup(null); }}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    setFlyoutTop(r.top);
                    setPinnedGroup(pinnedGroup === group.id ? null : group.id);
                    setOpenGroup(group.id);
                  }}
                  data-testid={`pilot-group-${group.id}`}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg font-bold text-sm transition ${
                    hasActive
                      ? 'bg-purple-50 text-purple-700'
                      : isOpen
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <GroupIcon size={16} /> {groupLabel}
                  </span>
                  <ChevronRight
                    size={14}
                    className={`text-gray-400 transition-transform ${isOpen ? 'translate-x-0.5' : ''}`}
                  />
                </button>

                {isOpen && (
                  <div
                    data-testid={`pilot-flyout-${group.id}`}
                    className="fixed z-[1000] w-64 bg-white rounded-xl shadow-2xl border border-gray-200 py-2"
                    style={{
                      top: Math.max(8, flyoutTop),
                      left: '16.5rem',
                      maxHeight: 'calc(100vh - 1rem)',
                      overflowY: 'auto',
                      direction: document.documentElement.getAttribute('dir') === 'rtl' ? 'rtl' : 'ltr',
                    }}
                    onMouseEnter={() => setOpenGroup(group.id)}
                    onMouseLeave={() => { if (!pinnedGroup) setOpenGroup(null); }}
                  >
                    <div className="px-3 py-2 border-b border-gray-100 mb-1">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <GroupIcon size={12} /> {groupLabel}
                      </p>
                    </div>
                    {group.items.map((item) => {
                      const ItemIcon = item.icon || LayoutDashboard;
                      const active = isActive(item.path);
                      return (
                        <Link
                          key={item.id}
                          to={item.path}
                          data-testid={`pilot-nav-${item.id}`}
                          onClick={() => {
                            setPinnedGroup(null);
                            setOpenGroup(null);
                            if (onClose) onClose();
                          }}
                          className={`flex items-center gap-2 mx-2 px-3 py-2 rounded-lg text-sm transition ${
                            active
                              ? 'bg-purple-50 text-purple-700 font-semibold'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <ItemIcon size={14} />
                          <span className="truncate">{renderLabel(item)}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pilot-mode hint at the bottom */}
        <div className="mt-6 mx-1 p-3 rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100">
          <p className="text-xs font-bold text-indigo-700 mb-1">
            {langMode === 'both' ? 'Pilot Snapshot / النسخة التجريبية' : (lang === 'ar' ? 'النسخة التجريبية' : 'Pilot Snapshot')}
          </p>
          <p className="text-[11px] text-gray-600 leading-relaxed">
            {lang === 'ar'
              ? 'الصفحات الظاهرة محدّدة من قِبل المدير عبر صلاحيات الدور.'
              : 'Visible pages are controlled by your admin via Role Permissions.'}
          </p>
          {feedbackEmail && (
            <a
              href={`mailto:${feedbackEmail}?subject=Pilot%20Feedback%20-%20${encodeURIComponent(identity?.name || 'Workspace')}`}
              data-testid="pilot-feedback-link"
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 underline decoration-dotted"
            >
              {lang === 'ar' ? '📨 أرسل ملاحظة' : '📨 Send feedback'}
              <span className="opacity-60">·</span>
              <span className="font-mono">{feedbackEmail}</span>
            </a>
          )}
        </div>
      </nav>

      {/* User block — name, role, profile, install, logout */}
      <div className="border-t border-gray-200 p-3 bg-gray-50" data-testid="pilot-user-block">
        {user && (
          <div className="flex items-center gap-2 px-2 py-2 mb-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
              {(user.full_name || user.email || '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-gray-900 truncate" data-testid="pilot-user-name">
                {user.full_name || user.email}
              </p>
              <p className="text-[11px] text-gray-500 capitalize">{user.role}</p>
            </div>
          </div>
        )}

        {!isAppInstalled && installPrompt && (
          <button
            data-testid="pilot-install-app"
            onClick={handleInstall}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-bold hover:from-indigo-700 hover:to-purple-700 transition mb-1.5"
          >
            <Download className="w-3.5 h-3.5" />
            {lang === 'ar' ? 'تثبيت التطبيق' : 'Install App'}
          </button>
        )}

        <Link
          to="/profile"
          data-testid="pilot-my-profile"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white text-gray-700 text-xs font-semibold transition mb-1"
        >
          <UserIcon className="w-3.5 h-3.5" />
          {lang === 'ar' ? 'الملف الشخصي' : 'My Profile'}
        </Link>

        <button
          data-testid="pilot-logout"
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold transition"
        >
          <LogOut className="w-3.5 h-3.5" />
          {lang === 'ar' ? 'تسجيل الخروج' : 'Logout'}
        </button>
      </div>
    </div>
  );
};

export default PilotSidebar;
